import Component from '@glimmer/component'
import type { TOC } from '@ember/component/template-only'
import { service } from '@ember/service'
import { t } from 'ember-intl'
import { or } from 'ember-truth-helpers'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import writingLegendLyingDown from '@images/writing-legend-lying-down.png'
import mangoStanding from '@images/mango-standing.png'
import gizmoStanding from '@images/gizmo-standing.png'

interface Signature {
  Element: HTMLDivElement
  Args: {
    compact?: boolean
    title?: string
    body?: string
  }
  Blocks: {
    default: []
  }
}

/**
 * Intended for use in non-reporting areas, whens searching brings up no results. Similar to NoDataMessage.
 */
export const NoResultsMessage: TOC<Signature> = <template>
  <div data-test-no-results-message class="text-dusty-black-300 space-y-2 text-center" ...attributes>
    {{#if (has-block)}}
      {{yield}}
    {{else if @compact}}
      <NoResultsMessageImage class="max-w-28" />
      <NoResultsMessageBody class="font-bold" @body={{or @body (t "components.noResultsMessage.bodyCompact")}} />
    {{else}}
      <NoResultsMessageImage class="max-w-xs" />
      <NoResultsMessageTitle @title={{or @title (t "components.noResultsMessage.title")}} />
      <NoResultsMessageBody class="text-xl" @body={{or @body (t "components.noResultsMessage.body")}} />
    {{/if}}
  </div>
</template>

interface ImageSignature {
  Element: HTMLImageElement
}

interface ImageData {
  url: string
  alt: string
}

export class NoResultsMessageImage extends Component<ImageSignature> {
  @service subscriptionType!: SubscriptionTypeService

  get image(): ImageData {
    const images: Record<SubscriptionType, ImageData> = {
      [SubscriptionType.Reading]: {
        url: gizmoStanding,
        alt: 'gizmo',
      },
      [SubscriptionType.Maths]: {
        url: mangoStanding,
        alt: 'mango the monkey',
      },
      [SubscriptionType.Writing]: {
        url: writingLegendLyingDown,
        alt: 'writing legend lying down',
      },
    }
    return images[this.subscriptionType.current ?? SubscriptionType.Reading]
  }

  <template>
    <img data-test-no-results-image class="mx-auto" src={{this.image.url}} alt={{this.image.alt}} ...attributes />
  </template>
}

interface TitleSignature {
  Element: HTMLDivElement
  Args: {
    title?: string
  }
}

export const NoResultsMessageTitle: TOC<TitleSignature> = <template>
  <h2 data-test-no-results-title ...attributes>
    {{@title}}
  </h2>
</template>

interface BodySignature {
  Element: HTMLDivElement
  Args: {
    body?: string
  }
}

export const NoResultsMessageBody: TOC<BodySignature> = <template>
  <p data-test-no-results-body ...attributes>
    {{@body}}
  </p>
</template>

export default NoResultsMessage
